import { ExclamationCircleIcon, PlusIcon, XIcon } from "@heroicons/react/outline";
import { InfiniteData } from "@tanstack/react-query";
import { FC, RefObject, useMemo, useRef, useState } from "react";

import LoadingSpinner from "./LoadingSpinner";
import { Workflow, WorkflowVersion, WorkflowVersionResponse } from "./Workflows/types";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useIntersectionObserver } from "@/hooks";
import {
  usePromptRegistryObjects,
  usePromptVersions,
  useWorkflows,
  useWorkflowVersions,
} from "@/queries";
import { ListPromptVersions } from "@/types";
import { Conditional, DynamicReleaseLabel } from "@/types/conditionals";
import { PaginatedPromptRegistryObjects, PromptRegistry } from "@/types/prompt-registry";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Version = WorkflowVersion | any;
type RegistryObject = PromptRegistry | Workflow;

interface RegistryState {
  id: number;
  registry: RegistryObject;
  version?: Version;
}

interface EditRegistryObjectsModalProps {
  initialRegistryIds: number[];
  isOpen: boolean;
  labelName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setConditionals: (conditionals: any) => void;
  setOpen: (isOpen: boolean) => void;
  isWorkflow?: boolean;
}

const EditRegistryObjectsModal: FC<EditRegistryObjectsModalProps> = ({
  initialRegistryIds,
  isOpen,
  labelName,
  setConditionals,
  setOpen,
  isWorkflow = false,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedRegistries, setSelectedRegistries] = useState<Map<number, RegistryState>>(
    new Map(),
  );
  const [selectedDropdownRegistry, setSelectedDropdownRegistry] = useState<RegistryObject | null>(
    null,
  );
  const [hasChanges, setHasChanges] = useState(false);

  const { userToken } = useAuth()!;
  const { activeWorkspaceId: workspaceId } = useUser();
  const ref = useRef<HTMLDivElement>(null);
  const entry = useIntersectionObserver(ref as RefObject<Element>, {});

  const resourceType = isWorkflow ? "workflow" : "prompt";

  useMemo(() => {
    if (initialRegistryIds.length > 0 && selectedRegistries.size === 0) {
      const newMap = new Map<number, RegistryState>();
      initialRegistryIds.forEach(id => {
        newMap.set(id, { id, registry: {} as RegistryObject });
      });
      setSelectedRegistries(newMap);
    }
  }, [initialRegistryIds, selectedRegistries.size]);

  const workflowsQuery = useWorkflows(workspaceId!.toString(), userToken!, labelName);

  const promptRegistryQuery = usePromptRegistryObjects(
    userToken!,
    {
      workspaceId: workspaceId!,
      promptLabelName: labelName,
    },
    Number.MAX_SAFE_INTEGER,
  );

  const registryObjects = useMemo(
    () => (isWorkflow ? workflowsQuery : promptRegistryQuery),
    [isWorkflow, workflowsQuery, promptRegistryQuery],
  );

  const workflowVersionsQuery = useWorkflowVersions(
    isWorkflow ? String(selectedDropdownRegistry?.id) : undefined,
    userToken!,
  );

  const promptVersionsQuery = usePromptVersions(userToken!, {
    workspaceId: workspaceId!,
    label: labelName,
    perPage: Number.MAX_SAFE_INTEGER,
    promptRegistryId: !isWorkflow ? selectedDropdownRegistry?.id : undefined,
  });

  const versionsQuery = useMemo(
    () => (isWorkflow ? workflowVersionsQuery : promptVersionsQuery),
    [isWorkflow, workflowVersionsQuery, promptVersionsQuery],
  );

  const versions = useMemo(() => {
    if (isWorkflow) {
      return (versionsQuery.data as WorkflowVersionResponse)?.workflow_versions || [];
    }
    return (
      (versionsQuery.data as InfiniteData<ListPromptVersions>)?.pages?.flatMap(
        (page: ListPromptVersions) => page.prompt_versions,
      ) || []
    );
  }, [isWorkflow, versionsQuery.data]);

  const registryObjectsList = useMemo(() => {
    if (isWorkflow) {
      return (
        registryObjects.data?.pages?.flatMap(
          page => (page as unknown as { success: boolean; workflows: Workflow[] }).workflows,
        ) || []
      );
    }
    return (
      registryObjects.data?.pages?.flatMap(
        page => (page as PaginatedPromptRegistryObjects).items,
      ) || []
    );
  }, [isWorkflow, registryObjects.data]);

  if (entry?.isIntersecting) {
    registryObjects.fetchNextPage();
  }

  const getVersionWithLabel = (registryId: number): Version | undefined => {
    try {
      const registry = registryObjectsList.find(template => template.id === registryId);

      if (!registry) {
        return undefined;
      }

      const filteredVersions = isWorkflow
        ? versions.filter(version => (version as WorkflowVersion).workflow_id === registry.id)
        : versions.filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            version => (version as any).prompt_id === registry.id,
          );

      return filteredVersions.find(version =>
        version.labels.some((label: { name: string }) => label.name === labelName),
      );
    } catch (error) {
      setErrorMessage(
        `Error finding ${resourceType} version: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
      return undefined;
    }
  };

  const createDynamicReleaseLabel = (registryId: number, version: Version): DynamicReleaseLabel =>
    ({
      is_real_label: true,
      percentage: 100,
      ...(isWorkflow
        ? {
            workflow_id: registryId,
            workflow_version_id: version.id,
            workflow_version_number: version.number,
          }
        : {
            prompt_registry_id: registryId,
            prompt_version_id: version.id,
            prompt_version_number: version.number,
          }),
    }) as DynamicReleaseLabel;

  const getRegistryName = (registry: RegistryObject): string =>
    isWorkflow
      ? (registry as Workflow).name || "Unnamed Workflow"
      : (registry as PromptRegistry).prompt_name;

  const handleAddRegistry = () => {
    if (!selectedDropdownRegistry) {
      return;
    }

    try {
      const version = getVersionWithLabel(selectedDropdownRegistry.id);

      if (!version) {
        throw new Error(`No version with label "${labelName}" found`);
      }

      setSelectedRegistries(prev => {
        const newMap = new Map(prev);
        newMap.set(selectedDropdownRegistry.id, {
          id: selectedDropdownRegistry.id,
          registry: selectedDropdownRegistry,
          version,
        });
        return newMap;
      });

      setSelectedDropdownRegistry(null);
      setHasChanges(true);
    } catch (error) {
      setErrorMessage(
        `Failed to add ${resourceType}: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  };

  const handleRemoveRegistry = (id: number) => {
    setSelectedRegistries(prev => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
    setHasChanges(true);
  };

  const handleSubmit = () => {
    try {
      const initialSet = new Set(initialRegistryIds);
      const currentSet = new Set(Array.from(selectedRegistries.keys()));

      const newRegistryIds = Array.from(currentSet).filter(id => !initialSet.has(id));
      const deletedRegistryIds = Array.from(initialSet).filter(id => !currentSet.has(id));

      setConditionals((prevConditionals: Conditional[]) => {
        const filteredConditionals = prevConditionals.map(conditional => ({
          ...conditional,
          dynamic_release_labels: conditional.dynamic_release_labels.filter(
            label =>
              !deletedRegistryIds.includes(
                isWorkflow
                  ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (label as any).workflow_registry_id
                  : label.prompt_registry_id,
              ),
          ),
        }));

        return filteredConditionals.map(conditional => {
          if (conditional.is_default && newRegistryIds.length > 0) {
            const newLabels = newRegistryIds
              .map(id => {
                const registryState = selectedRegistries.get(id);
                if (!registryState?.version) {
                  const version = getVersionWithLabel(id);
                  if (!version) {
                    return null;
                  }
                  return createDynamicReleaseLabel(id, version);
                }
                return createDynamicReleaseLabel(id, registryState.version);
              })
              .filter(Boolean) as DynamicReleaseLabel[];

            return {
              ...conditional,
              dynamic_release_labels: [...conditional.dynamic_release_labels, ...newLabels],
            };
          }
          return conditional;
        });
      });

      setOpen(false);
    } catch (error) {
      setErrorMessage(
        `Error updating ${resourceType} templates: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  };

  const isLoadingRegistryObjects = registryObjects.isLoading;
  const selectedRegistryIds = Array.from(selectedRegistries.keys());
  const noAvailableRegistries =
    registryObjectsList.filter(registry => !selectedRegistryIds.includes(registry.id)).length === 0;

  return (
    <Dialog onOpenChange={setOpen} open={isOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Choose {resourceType.charAt(0).toUpperCase() + resourceType.slice(1)}s{" "}
          </DialogTitle>
          <DialogDescription>
            Select the {resourceType}s to include in this experiment.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {isLoadingRegistryObjects && (
            <div ref={ref} className="mt-4 flex justify-center py-2">
              <LoadingSpinner size={5} />
            </div>
          )}
          {!isLoadingRegistryObjects && selectedRegistryIds.length > 0 && (
            <div className="flex max-h-[400px] flex-wrap gap-2 overflow-y-auto">
              {registryObjectsList
                .filter(registry => selectedRegistryIds.includes(registry.id))
                .map(registry => (
                  <div
                    key={registry.id}
                    className="group flex items-center rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-xs transition-colors duration-200 hover:bg-gray-200"
                  >
                    <span className="mr-0.5 break-all">{getRegistryName(registry)}</span>
                    <button
                      onClick={() => handleRemoveRegistry(registry.id)}
                      className="p-0.75 ml-2 flex-shrink-0 rounded-full transition-colors duration-200 hover:bg-gray-300"
                    >
                      <XIcon className="h-3.5 w-3.5 text-gray-400 transition-colors duration-200 group-hover:text-red-500" />
                    </button>
                  </div>
                ))}
            </div>
          )}
          <div className="space-y-2 pb-2">
            <label htmlFor="registry-select" className="block text-sm font-medium text-gray-700">
              Available {resourceType}s
            </label>
            <div className="flex items-center space-x-2">
              <DropdownMenu>
                <DropdownMenuTrigger className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-left shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
                  {selectedDropdownRegistry ? (
                    getRegistryName(selectedDropdownRegistry)
                  ) : (
                    <span className="text-gray-500">Select {resourceType}...</span>
                  )}
                </DropdownMenuTrigger>
                <DropdownMenuContent className="mt-1 w-full">
                  {registryObjectsList
                    .filter(registry => !selectedRegistryIds.includes(registry.id))
                    .map(registry => (
                      <DropdownMenuItem
                        key={registry.id}
                        onSelect={() => setSelectedDropdownRegistry(registry)}
                        className="cursor-pointer px-3 py-2 hover:bg-blue-50"
                      >
                        {getRegistryName(registry)}
                      </DropdownMenuItem>
                    ))}
                  {noAvailableRegistries && (
                    <DropdownMenuItem className="cursor-not-allowed px-3 py-2 italic text-gray-400">
                      All {resourceType} templates have been added.
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={handleAddRegistry} disabled={!selectedDropdownRegistry}>
                <PlusIcon className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
        {errorMessage && (
          <div className="mt-6 max-h-[500px] overflow-y-auto rounded-md border border-red-300 bg-red-50 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationCircleIcon className="h-6 w-6 text-red-500" aria-hidden="true" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-red-800">Error</h3>
                <div className="mt-2 text-base text-red-700">
                  <p>{errorMessage}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={selectedRegistryIds.length === 0 || !hasChanges}>
            Update Experiment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditRegistryObjectsModal;
